import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, inject, Renderer2, viewChild } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { OnlinePaymentAnnouncementAction } from './online-payment-announcement-action.enum';
import { ONLINE_PAYMENT_ANNOUNCEMENT_ILLUSTRATION, ONLINE_PAYMENT_ANNOUNCEMENT_MESSAGE_ID } from './online-payment-announcement.const';

@Component({
  selector: 'tiss-online-payment-announcement-dialog',
  imports: [ButtonModule, TranslocoPipe],
  templateUrl: './online-payment-announcement-dialog.component.html',
  styleUrl: './online-payment-announcement-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnlinePaymentAnnouncementDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly renderer = inject(Renderer2);
  private readonly root = viewChild.required<ElementRef<HTMLElement>>('root');

  protected readonly action = OnlinePaymentAnnouncementAction;
  protected readonly illustration = ONLINE_PAYMENT_ANNOUNCEMENT_ILLUSTRATION;
  protected readonly messageId = ONLINE_PAYMENT_ANNOUNCEMENT_MESSAGE_ID;

  constructor() {
    // The dialog opens with `focusOnShow` disabled: PrimeNG would focus the close button and the browser paints its
    // focus ring on a fresh page. The content wrapper takes the focus instead, so Escape and Tab keep working inside
    // the dialog while the focus ring shows only on the controls the user tabs to.
    //
    // With `showHeader: false` PrimeNG names the `role="dialog"` panel by a header id that is not rendered, and
    // `DynamicDialogConfig.ariaLabelledBy` is not read by the component (PrimeNG 19), so the message is wired to the
    // panel here (same approach as offer-promotion-badge for `aria-modal`).
    afterNextRender(() => {
      const root = this.root().nativeElement;
      const dialog = root.closest('[role="dialog"]');

      if (dialog) {
        this.renderer.setAttribute(dialog, 'aria-labelledby', ONLINE_PAYMENT_ANNOUNCEMENT_MESSAGE_ID);
      }

      root.focus({ preventScroll: true });
    });
  }

  protected close(action: OnlinePaymentAnnouncementAction): void {
    this.dialogRef.close(action);
  }
}
